import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import { ASC, DESC, AVERAGE, BAYSIAN } from "const"
import { QidProduct } from "server/models"
import { OrderType } from "client/utils"
import {
    StoreType,
    QidProductFilters,
    SortQidProducts,
    setSilModelDevicesQidProductIds,
    setSilModelDevicesQidProductSort,
    setSilModelDevicesQidProductFilters,
    setSilModelDevicesQidCalculationMethod,
} from "client/store"
import {
    PageContent,
    TableSearchWrapper,
    TableWrapper,
    TableScroller,
    TableContentWrapper,
    TableRow,
    TableHeaderCell,
    TableCell,
    TableCellContentWrapper,
    SortControl,
    Empty,
    SelectDropdown,
    SelectDropdownItem,
} from "client/components"

import QidProductSearch from "client/pages/search/qidProductSearch"
import Columns from "./columns"
import Rows from "./rows"


const QidProducts = () => {
    const productList = useSelector((state: StoreType) => state.silModelDevices.qidProducts.productList)
    const productIds = useSelector((state: StoreType) => state.silModelDevices.qidProducts.productIds)
    const sortColumn = useSelector((state: StoreType) => state.silModelDevices.qidProducts.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.silModelDevices.qidProducts.sortOrder)
    const filters = useSelector((state: StoreType) => state.silModelDevices.qidProducts.filters)
    const calculationMethod = useSelector((state: StoreType) => state.silModelDevices.qidProducts.calculationMethod)
    const lang = useSelector((state: StoreType) => state.system.lang)

    const dispatch = useDispatch()

    useEffect(() => {
        const sortedIds = SortQidProducts(Object.values(productList), sortColumn, sortOrder, filters)
        dispatch(setSilModelDevicesQidProductIds(sortedIds))
    }, [productList, sortColumn, sortOrder, filters])

    const onSort = (column: keyof QidProduct) => {
        let order: OrderType
        if (column === sortColumn) {
            order = sortOrder === ASC ? DESC : ASC
        } else order = ASC
        dispatch(setSilModelDevicesQidProductSort(column, order))
    }

    const onFilter = (newFilters: QidProductFilters) => {
        dispatch(setSilModelDevicesQidProductFilters(newFilters))
    }

    const columns = Columns()
    const rows = Rows(columns)

    return (
        <PageContent>
            <TableSearchWrapper>
                <QidProductSearch
                    id="productList"
                    rightOffset
                    filters={filters}
                    onFilter={onFilter}
                />

                <SelectDropdown search="true" title={lang[calculationMethod]}>
                    <SelectDropdownItem onClick={() => dispatch(setSilModelDevicesQidCalculationMethod(AVERAGE))}>
                        {lang.average}
                    </SelectDropdownItem>
                    <SelectDropdownItem onClick={() => dispatch(setSilModelDevicesQidCalculationMethod(BAYSIAN))}>
                        {lang.baysian}
                    </SelectDropdownItem>
                </SelectDropdown>
            </TableSearchWrapper>

            <TableWrapper>
                <TableScroller>
                    <TableContentWrapper>
                        <thead>
                            <TableRow size="sm" noHover>
                                {
                                    columns.productColumns
                                    .concat(columns.commonColumns, columns.resultColumns)
                                    .map((column) => (
                                        <TableHeaderCell
                                            key={column.field}
                                            cellWidth={column.cellWidth}
                                            cellMinWidth={column.cellMinWidth}
                                            cellMaxWidth={column.cellMaxWidth}
                                            onClick={() => column.sortable && onSort(column.field as keyof QidProduct)}
                                        >
                                            {
                                                column.header
                                                && <span>{column.header}</span>
                                            }
                                            {
                                                column.sortable
                                                && (
                                                    <SortControl
                                                        show={sortColumn === column.field}
                                                        direction={sortColumn === column.field ? sortOrder : DESC}
                                                    />
                                                )
                                            }
                                        </TableHeaderCell>
                                    ))
                                }
                            </TableRow>
                        </thead>
                        <tbody>
                            { rows }
                            {
                                productIds.length === 0 && (
                                    <TableRow size="sm">
                                        <TableCell colSpan={100}>
                                            <TableCellContentWrapper>
                                                <Empty />
                                            </TableCellContentWrapper>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                        </tbody>
                    </TableContentWrapper>
                </TableScroller>
            </TableWrapper>
        </PageContent>
    )
}
export default QidProducts
